// cspell:ignore firestore Firestore Mocktails
import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useRestaurant } from "./contexts/RestaurantContext";
import { menuService } from "./services/menuService";
import "./App.css";

function PublicMenu() {
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentRestaurant } = useRestaurant();

  // Get category and menu type from URL params or localStorage, default to 'All'
  const urlParams = new URLSearchParams(location.search);
  const categoryFromUrl = urlParams.get("category");
  const menuTypeFromUrl = urlParams.get("menuType");
  const [selectedCategory, setSelectedCategory] = useState(
    categoryFromUrl || localStorage.getItem("selectedCategory") || "All"
  );

  // Categories that shouldn't link to detail pages
  const simpleCategories = [
    "Specialty Cocktails",
    "Sangria by the Glass",
    "Mocktails",
    "Beers",
  ];

  useEffect(() => {
    const loadMenuItems = async () => {
      try {
        if (currentRestaurant?.id) {
          // Use the tenant-aware menu service to fetch items
          const unsubscribe = menuService.subscribeToMenuItems(
            currentRestaurant.id,
            (items) => {
              setAllItems(items);
              setLoading(false);
            },
            (error) => {
              console.error("Error fetching menu items:", error);
              setLoading(false);
            }
          );
          return () => unsubscribe && unsubscribe();
        } else {
          // Fallback: Load from enhanced_menu.json for legacy support
          try {
            const response = await fetch("/enhanced_menu.json");
            const menuData = await response.json();
            setAllItems(menuData);
            setLoading(false);
          } catch (error) {
            console.error("Error loading legacy menu:", error);
            setLoading(false);
          }
        }
      } catch (error) {
        console.error("Error loading menu items:", error);
        setLoading(false);
      }
    };

    loadMenuItems();
  }, [currentRestaurant?.id]);

  // Filter the list based on the selected category and menu type
  const filteredList = useMemo(() => {
    // Define category mappings for different menu types
    const menuTypeCategories = {
      wine: ["Wines by the Glass", "Wines by the Bottle"],
      beer: ["Draft Beer", "Bottled Beer", "Beers"],
      cocktails: ["Specialty Cocktails"],
      all: null, // null means show all categories
    };

    const allowedCategories = menuTypeCategories[menuTypeFromUrl || "all"];

    let itemsToShow = allItems.filter(
      (item) => item.inStock !== false || item.type
    );

    // Filter by menu type first if specified
    if (allowedCategories) {
      itemsToShow = itemsToShow.filter((item) =>
        allowedCategories.includes(item.category)
      );
    }

    // Then filter by selected category if not "All"
    if (selectedCategory !== "All") {
      itemsToShow = itemsToShow.filter(
        (item) => item.category === selectedCategory
      );
    }

    return itemsToShow;
  }, [allItems, selectedCategory, menuTypeFromUrl]);

  const categories = useMemo(() => {
    const uniqueCategories = [
      ...new Set(allItems.map((item) => item.category)),
    ];

    // Define category mappings for different menu types
    const menuTypeCategories = {
      wine: ["Wines by the Glass", "Wines by the Bottle"],
      beer: ["Draft Beer", "Bottled Beer", "Beers"],
      cocktails: ["Specialty Cocktails"],
      all: [
        "Specialty Cocktails",
        "Sangria by the Glass",
        "Beers",
        "Mocktails",
        "Wines by the Glass",
        "Wines by the Bottle",
        "Drink Your Dessert",
        "Draft Beer",
        "Bottled Beer",
      ],
    };

    // Get allowed categories based on menu type
    const allowedCategories =
      menuTypeCategories[menuTypeFromUrl || "all"] || menuTypeCategories.all;

    const categoryOrder = [
      "Specialty Cocktails",
      "Sangria by the Glass",
      "Draft Beer",
      "Bottled Beer",
      "Beers",
      "Mocktails",
      "Wines by the Glass",
      "Wines by the Bottle",
      "Drink Your Dessert",
      "All",
    ];

    // Filter to only include categories that exist in the data and are allowed for this menu type
    const orderedCategories = categoryOrder.filter(
      (category) =>
        category === "All" ||
        (uniqueCategories.includes(category) &&
          allowedCategories.includes(category) &&
          category !== "Spirits")
    );

    // Add any remaining categories that weren't in the predefined order (excluding Spirits)
    const remainingCategories = uniqueCategories
      .filter(
        (category) =>
          !categoryOrder.includes(category) &&
          category !== "Spirits" &&
          allowedCategories.includes(category)
      )
      .sort((a, b) => a.localeCompare(b));

    return [...orderedCategories, ...remainingCategories];
  }, [allItems, menuTypeFromUrl]);

  // Get menu title based on menu type
  const getMenuTitle = () => {
    const baseName = currentRestaurant?.name || "Lucky Dog Tavern";
    if (menuTypeFromUrl === "wine") return `${baseName} - Wine Menu`;
    if (menuTypeFromUrl === "beer") return `${baseName} - Beer Menu`;
    if (menuTypeFromUrl === "cocktails") return `${baseName} - Cocktail Menu`;
    return baseName;
  };

  // Determine if Compare Wines button should be visible
  const shouldShowCompareWines =
    ["Wines by the Glass", "Wines by the Bottle", "All"].includes(
      selectedCategory
    ) || menuTypeFromUrl === "wine";

  // Restore scroll position when loading completes - MUST be before any early returns
  useEffect(() => {
    let timeoutId;
    if (!loading) {
      const savedScrollPosition = localStorage.getItem("scrollPosition");
      if (savedScrollPosition) {
        // Use setTimeout to ensure DOM is ready
        timeoutId = setTimeout(() => {
          window.scrollTo(0, parseInt(savedScrollPosition));
          localStorage.removeItem("scrollPosition"); // Clean up after use
        }, 100);
      }
    }
    // Cleanup function to prevent memory leaks
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [loading]);

  // Memoize event handlers to prevent unnecessary re-renders
  // Function to handle category changes and preserve state
  const handleCategoryChange = useCallback(
    (category) => {
      setSelectedCategory(category);
      localStorage.setItem("selectedCategory", category);

      // Update URL without causing a page reload - include restaurant slug and menu type
      const baseUrl = currentRestaurant?.slug
        ? `/restaurant/${currentRestaurant.slug}/drinks`
        : "/drinks";

      let newUrl = baseUrl;
      const params = new URLSearchParams();

      if (menuTypeFromUrl) {
        params.set("menuType", menuTypeFromUrl);
      }

      if (category !== "All") {
        params.set("category", category);
      }

      if (params.toString()) {
        newUrl = `${baseUrl}?${params.toString()}`;
      }

      window.history.replaceState({}, "", newUrl);
    },
    [currentRestaurant?.slug, menuTypeFromUrl]
  );

  // Save scroll position when navigating away
  const handleItemClick = useCallback(() => {
    localStorage.setItem("scrollPosition", window.scrollY.toString());
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  if (loading) {
    return <div className="App">Loading menu...</div>;
  }

  // Helper function to render each item based on its type
  const renderItem = (item) => {
    switch (item.type) {
      case "mainHeader":
        return <h2 className="wine-main-header">{item.text}</h2>;
      case "countryHeader":
        return <h3 className="wine-country-header">{item.text}</h3>;
      case "regionHeader":
        return <h4 className="wine-region-header">{item.text}</h4>;
      case "item":
        // Check if this is a category that should show description directly
        if (simpleCategories.includes(item.category)) {
          return (
            <div className="drink-item-simple">
              <div className="wine-item">
                <span className="wine-name">
                  {item.bin && item.bin !== "0" ? `${item.bin}  ` : ""}
                  {item.name}
                </span>
                {item.price > 0 && (
                  <span className="wine-price">${item.price.toFixed(2)}</span>
                )}
              </div>
              {item.description && (
                <div className="drink-description">{item.description}</div>
              )}
            </div>
          );
        } else {
          // For wines and spirits, make it a clickable link to the detail page
          const detailUrl = currentRestaurant?.slug
            ? `/restaurant/${currentRestaurant.slug}/drink/${item.id}`
            : `/drink/${item.id}`;
          return (
            <Link
              to={detailUrl}
              className="drink-item-link"
              onClick={handleItemClick}
            >
              <div className="wine-item">
                <span className="wine-name">
                  {item.bin && item.bin !== "0" ? `${item.bin}  ` : ""}
                  {item.name}
                </span>
                {item.price > 0 && (
                  <span className="wine-price">${item.price.toFixed(2)}</span>
                )}
              </div>
            </Link>
          );
        }
      default:
        return null;
    }
  };

  return (
    <div className="App">
      {/* Skip links for keyboard navigation - ADA compliance */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <a href="#navigation" className="skip-link">
        Skip to navigation
      </a>

      <header
        className={`header-section ${
          shouldShowCompareWines ? "has-compare-btn" : "no-compare-btn"
        }`}
      >
        <button
          className="back-button"
          onClick={() =>
            navigate(
              currentRestaurant?.slug
                ? `/restaurant/${currentRestaurant.slug}`
                : "/"
            )
          }
          aria-label="Back to main menu"
        >
          ← Back
        </button>
        <h1 className="title">{getMenuTitle()}</h1>
        {shouldShowCompareWines && (
          <Link
            to={
              currentRestaurant?.slug
                ? `/restaurant/${currentRestaurant.slug}/compare`
                : "/compare"
            }
            className="compare-wines-btn"
            aria-label="Compare selected wines side by side"
          >
            Compare Wines
          </Link>
        )}
      </header>

      <nav
        id="navigation"
        className="categories"
        role="navigation"
        aria-label="Drink categories"
      >
        {categories.map((category) => (
          <button
            key={category}
            className={`category-button ${
              selectedCategory === category ? "active" : ""
            }`}
            onClick={() => handleCategoryChange(category)}
            aria-pressed={selectedCategory === category}
            style={{ fontSize: "2.5rem", padding: "1rem 2rem" }}
          >
            {category}
          </button>
        ))}
        {/* Separate Spirits button that navigates to spirits page */}
        <button
          className="category-button spirits-button"
          onClick={() =>
            navigate(
              currentRestaurant?.slug
                ? `/restaurant/${currentRestaurant.slug}/spirits`
                : "/spirits"
            )
          }
          aria-label="View spirits menu"
          style={{
            fontSize: "2.5rem",
            padding: "1rem 2rem",
            backgroundColor: "#c39616",
            color: "#31231f",
          }}
        >
          Spirits
        </button>
      </nav>

      <section
        id="main-content"
        className="list-container"
        aria-label={`${selectedCategory} drinks list`}
      >
        {filteredList.length > 0 ? (
          <ul>
            {filteredList.map((item) => (
              <li key={item.id || item.text}>{renderItem(item)}</li>
            ))}
          </ul>
        ) : (
          <p aria-live="polite">No drinks available in this category.</p>
        )}
      </section>
    </div>
  );
}

export default PublicMenu;
